/* eslint-disable max-lines */

import { LoggedInUser } from '../../../../types/LoggedInUser';
import { NamePlaceholders } from '../../../../types/NamePlaceholders';
import { SelectAttribute } from '../../../../types/SelectAttribute';
import { ButtonText } from '../../../Button/ButtonText';
import { DropZone } from '../../../DropZone';
import { FormListInputAndText } from '../../../Form/FormList/FormListInputAndText';
import { FormListName } from '../../../Form/FormList/FormListName';
import { FromListBirthdate } from '../../../Form/FormList/FromListBirthdate';
import { InputText } from '../../../Form/InputText';
import { Select } from '../../../Form/Select';
import { TextArea } from '../../../Form/TextArea';
import { InnerButtonMargin } from '../../../Layout/InnerLayout/InnerButtonMargin';
import { InnerTabSpace } from '../../../Layout/InnerLayout/InnerTabSpace';
import { TableBody } from '../../../Table/TableBody';
import { TableTrFormLabel } from '../../../Table/TableTr/TableTrFromLabel';

import { InvalidErrors } from './createInvalidErrors';
import { createGenderAttributes } from './gender';
import { createHasChildrenAttributes, HasChildrenId } from './hasChildren';
import {
  createMarriageStatusAttributes,
  MarriageStatusId,
} from './marriageStatus';
import { createPrefectureAttributes, PrefectureId } from './prefecture';
import { InitBasicInfo } from './useProfileSettingForm';

import type { FC, ChangeEventHandler, FocusEventHandler } from 'react';

import { BirthMonth } from '@/packages/nurse-senka-frontend-sdk';

type Props = {
  loggedInUser: LoggedInUser;
  onChangePreferredUsername: ChangeEventHandler<HTMLInputElement>;
  onChangeFamilyName: ChangeEventHandler<HTMLInputElement>;
  onChangeGivenName: ChangeEventHandler<HTMLInputElement>;
  onChangeFamilyNameKana: ChangeEventHandler<HTMLInputElement>;
  onChangeGivenNameKana: ChangeEventHandler<HTMLInputElement>;
  onChangeGender: ChangeEventHandler<HTMLSelectElement>;
  onChangeBirthYear: ChangeEventHandler<HTMLSelectElement>;
  onChangeBirthMonth: ChangeEventHandler<HTMLSelectElement>;
  onChangeBirthday: ChangeEventHandler<HTMLSelectElement>;
  onChangePhoneNumber: ChangeEventHandler<HTMLInputElement>;
  onChangePostalCode: ChangeEventHandler<HTMLInputElement>;
  handlePostalCodeFocusEvent: FocusEventHandler<HTMLInputElement>;
  onChangePrefectureId: ChangeEventHandler<HTMLSelectElement>;
  onChangeLocality: ChangeEventHandler<HTMLInputElement>;
  onChangeStreetAddress: ChangeEventHandler<HTMLInputElement>;
  onChangeBuilding: ChangeEventHandler<HTMLInputElement>;
  onChangeMarriageStatusId: ChangeEventHandler<HTMLSelectElement>;
  onChangeHasChildrenId: ChangeEventHandler<HTMLSelectElement>;
  handleFileUpload: ChangeEventHandler<HTMLInputElement>;
  onChangeSelfIntroduction: ChangeEventHandler<HTMLTextAreaElement>;
  locality: string;
  nameErrorMessage: string;
  kanaErrorMessage: string;
  namePlaceholders: NamePlaceholders;
  kanaPlaceholders: NamePlaceholders;
  birthYearAttributes: SelectAttribute[];
  birthMonthAttributes: SelectAttribute[];
  birthdayAttributes: SelectAttribute[];
  selectedBirthYear: number;
  selectedBirthMonth: BirthMonth;
  selectedBirthday: number;
  selectedPrefectureId: PrefectureId;
  selectedMarriageStatusId: MarriageStatusId;
  selectedHasChildrenId: HasChildrenId;
  imagePreviewUrl: string;
  profileImageMessage: string;
  selfIntroduction: string;
  initBasicInfo?: InitBasicInfo;
  invalidErrors?: InvalidErrors;
};

// eslint-disable-next-line max-lines-per-function, complexity
export const BasicInfoTabPanel: FC<Props> = ({
  loggedInUser,
  onChangePreferredUsername,
  onChangeFamilyName,
  onChangeGivenName,
  onChangeFamilyNameKana,
  onChangeGivenNameKana,
  onChangeGender,
  onChangeBirthYear,
  onChangeBirthMonth,
  onChangeBirthday,
  onChangePhoneNumber,
  onChangePostalCode,
  handlePostalCodeFocusEvent,
  onChangePrefectureId,
  onChangeLocality,
  onChangeStreetAddress,
  onChangeBuilding,
  onChangeMarriageStatusId,
  onChangeHasChildrenId,
  handleFileUpload,
  onChangeSelfIntroduction,
  locality,
  nameErrorMessage,
  kanaErrorMessage,
  namePlaceholders,
  kanaPlaceholders,
  birthYearAttributes,
  birthMonthAttributes,
  birthdayAttributes,
  selectedBirthYear,
  selectedBirthMonth,
  selectedBirthday,
  selectedPrefectureId,
  selectedMarriageStatusId,
  selectedHasChildrenId,
  imagePreviewUrl,
  profileImageMessage,
  selfIntroduction,
  initBasicInfo,
  invalidErrors,
}) => (
  <InnerTabSpace>
    <TableBody>
      <TableTrFormLabel
        id="profileImage"
        required={false}
        errorMessage={profileImageMessage}
        title="プロフィール画像"
      >
        <DropZone
          loggedInUser={loggedInUser}
          imagePreviewUrl={imagePreviewUrl}
          handleFileUpload={handleFileUpload}
        />
      </TableTrFormLabel>
      <TableTrFormLabel
        id="preferredUsername"
        required={true}
        errorMessage={
          invalidErrors?.preferredUsernameError?.reason
            ? invalidErrors?.preferredUsernameError?.reason
            : ''
        }
        title="ニックネーム"
      >
        <FormListInputAndText text="投稿した際に表示される名称になります">
          <InputText
            name="preferredUsername"
            id="preferredUsername"
            type="text"
            width="default"
            placeholder=""
            onChange={onChangePreferredUsername}
            defaultValue={initBasicInfo?.preferredUsername}
          />
        </FormListInputAndText>
      </TableTrFormLabel>
      <TableTrFormLabel
        id="selfIntroduction"
        required={false}
        errorMessage={
          invalidErrors?.selfIntroductionError?.reason
            ? invalidErrors?.selfIntroductionError?.reason
            : ''
        }
        title="自己紹介"
      >
        <TextArea
          id="selfIntroduction"
          width="wide"
          placeholder="自己紹介（500文字以内）"
          onChange={onChangeSelfIntroduction}
          defaultValue={selfIntroduction}
        />
      </TableTrFormLabel>
      <TableTrFormLabel
        id="familyName"
        required={true}
        errorMessage={
          invalidErrors?.familyNameError?.reason ||
          invalidErrors?.givenNameError?.reason
            ? nameErrorMessage
            : ''
        }
        title="氏名"
      >
        <FormListName
          familyNameLabel="姓"
          givenNameLabel="名"
          placeholders={namePlaceholders}
          handleChangeFamilyName={onChangeFamilyName}
          handleChangeGivenName={onChangeGivenName}
          givenNameDefaultValue={initBasicInfo?.givenName}
          familyNameDefaultValue={initBasicInfo?.familyName}
        />
      </TableTrFormLabel>
      <TableTrFormLabel
        id="familyName"
        required={true}
        errorMessage={
          invalidErrors?.familyNameKanaError?.reason ||
          invalidErrors?.givenNameKanaError?.reason
            ? kanaErrorMessage
            : ''
        }
        title="ふりがな"
      >
        <FormListName
          familyNameLabel="せい"
          givenNameLabel="めい"
          placeholders={kanaPlaceholders}
          handleChangeFamilyName={onChangeFamilyNameKana}
          handleChangeGivenName={onChangeGivenNameKana}
          givenNameDefaultValue={initBasicInfo?.givenNameKana}
          familyNameDefaultValue={initBasicInfo?.familyNameKana}
        />
      </TableTrFormLabel>
      <TableTrFormLabel
        id="gender"
        required={true}
        errorMessage={
          invalidErrors?.genderError?.reason
            ? invalidErrors?.genderError?.reason
            : ''
        }
        title="性別"
      >
        {initBasicInfo?.gender ? (
          <Select
            id="gender"
            width="default"
            attributes={createGenderAttributes()}
            onChange={onChangeGender}
            defaultValue={String(initBasicInfo?.gender)}
          />
        ) : (
          <Select
            id="gender"
            width="default"
            attributes={createGenderAttributes()}
            onChange={onChangeGender}
          />
        )}
      </TableTrFormLabel>
      <TableTrFormLabel
        id="birthYear"
        required={true}
        errorMessage={
          invalidErrors?.birthdateError?.reason
            ? invalidErrors?.birthdateError?.reason
            : ''
        }
        title="生年月日"
      >
        <FromListBirthdate
          birthYearAttributes={birthYearAttributes}
          birthMonthAttributes={birthMonthAttributes}
          birthdayAttributes={birthdayAttributes}
          handleChangeBirthYear={onChangeBirthYear}
          handleChangeBirthMonth={onChangeBirthMonth}
          handleChangeBirthday={onChangeBirthday}
          selectedBirthYear={String(selectedBirthYear)}
          selectedBirthMonth={String(selectedBirthMonth)}
          selectedBirthday={String(selectedBirthday)}
        />
      </TableTrFormLabel>
      <TableTrFormLabel
        id="email"
        required={true}
        errorMessage={
          invalidErrors?.emailError?.reason
            ? invalidErrors?.emailError?.reason
            : ''
        }
        title="メールアドレス"
      >
        <InnerButtonMargin type="right">
          <InputText
            disable
            name="email"
            id="email"
            type="email"
            width="default"
            placeholder="nurse-senka@exmple.com"
            defaultValue={loggedInUser.email}
          />
        </InnerButtonMargin>
        <ButtonText color="default" link="/my/emails" useNextLink={true}>
          変更する
        </ButtonText>
      </TableTrFormLabel>
      <TableTrFormLabel
        id="phoneNumber"
        required={false}
        errorMessage={
          invalidErrors?.phoneNumberError?.reason
            ? invalidErrors?.phoneNumberError?.reason
            : ''
        }
        title="電話番号"
      >
        <InputText
          name="phoneNumber"
          id="phoneNumber"
          type="tel"
          width="default"
          placeholder="ハイフンなしの10桁〜11桁"
          onChange={onChangePhoneNumber}
          defaultValue={initBasicInfo?.phoneNumber}
        />
      </TableTrFormLabel>
      <TableTrFormLabel
        id="postalCode"
        required={true}
        errorMessage={
          invalidErrors?.postalCodeError?.reason
            ? invalidErrors?.postalCodeError?.reason
            : ''
        }
        title="郵便番号"
      >
        <InputText
          name="postalCode"
          id="postalCode"
          type="text"
          width="default"
          placeholder="ハイフンなしの数値7桁"
          onChange={onChangePostalCode}
          onBlur={handlePostalCodeFocusEvent}
          defaultValue={initBasicInfo?.postalCode}
        />
      </TableTrFormLabel>
      <TableTrFormLabel
        id="prefecture"
        required={true}
        errorMessage={
          invalidErrors?.prefectureIdError?.reason
            ? invalidErrors?.prefectureIdError?.reason
            : ''
        }
        title="都道府県"
      >
        {selectedPrefectureId ? (
          <Select
            id="prefecture"
            width="default"
            attributes={createPrefectureAttributes()}
            onChange={onChangePrefectureId}
            value={String(selectedPrefectureId)}
          />
        ) : (
          <Select
            id="prefecture"
            width="default"
            attributes={createPrefectureAttributes()}
            onChange={onChangePrefectureId}
            defaultValue={String(selectedPrefectureId)}
          />
        )}
      </TableTrFormLabel>
      <TableTrFormLabel
        id="locality"
        required={true}
        errorMessage={
          invalidErrors?.localityError?.reason
            ? invalidErrors?.localityError?.reason
            : ''
        }
        title="市区町村"
      >
        <InputText
          name="locality"
          id="locality"
          type="text"
          width="default"
          placeholder="港区芝公園"
          onChange={onChangeLocality}
          value={locality}
        />
      </TableTrFormLabel>
      <TableTrFormLabel
        id="streetAddress"
        required={true}
        errorMessage={
          invalidErrors?.streetAddressError?.reason
            ? invalidErrors?.streetAddressError?.reason
            : ''
        }
        title="番地"
      >
        <InputText
          name="streetAddress"
          id="streetAddress"
          type="text"
          width="default"
          placeholder="2-11-1"
          onChange={onChangeStreetAddress}
          defaultValue={initBasicInfo?.streetAddress}
        />
      </TableTrFormLabel>
      <TableTrFormLabel
        id="building"
        required={false}
        errorMessage={
          invalidErrors?.buildingError?.reason
            ? invalidErrors?.buildingError?.reason
            : ''
        }
        title="建物名"
      >
        <InputText
          name="building"
          id="building"
          type="text"
          width="default"
          placeholder="住友不動産芝公園タワー"
          onChange={onChangeBuilding}
          defaultValue={initBasicInfo?.building}
        />
      </TableTrFormLabel>

      <TableTrFormLabel
        id="marriageStatus"
        required={true}
        errorMessage={
          invalidErrors?.marriageStatusError?.reason
            ? invalidErrors?.marriageStatusError?.reason
            : ''
        }
        title="結婚状況"
      >
        <Select
          id="marriageStatus"
          width="default"
          attributes={createMarriageStatusAttributes()}
          onChange={onChangeMarriageStatusId}
          defaultValue={String(selectedMarriageStatusId)}
        />
      </TableTrFormLabel>
      <TableTrFormLabel
        id="hasChildren"
        required={true}
        errorMessage={
          invalidErrors?.hasChildrenError?.reason
            ? invalidErrors?.hasChildrenError?.reason
            : ''
        }
        title="子供の有無"
      >
        <Select
          id="hasChildren"
          width="default"
          attributes={createHasChildrenAttributes()}
          onChange={onChangeHasChildrenId}
          defaultValue={String(selectedHasChildrenId)}
        />
      </TableTrFormLabel>
    </TableBody>
  </InnerTabSpace>
);
